import React, { useEffect, useState } from 'react';
import type { CashflowPayload, Filters, FoundationPayload } from '../types/biApp';
import { CashflowCockpit, BalanceCorrectionPanel } from './cashflowModule';
import { PaymentSelectionModule } from './paymentSelectionModule';
import { PaymentBatchesModule } from './paymentBatchesModule';
import { DateInput } from '../shared/DateInput';
import { ContractsModule } from './contractsModule';
import { InflowsTab } from './treasuryInflowsTab';

type UserLike = { role?: string; allowed_modules?: string[]; email?: string | null } | null;
type TreasuryTab = 'pregled' | 'placanja' | 'priljevi' | 'racuni' | 'ira' | 'nalozi' | 'potpis' | 'ispravak' | 'ugovori' | 'postavke';
type PaymentsSubTab = 'odabir' | 'nalozi';

const T = { bg: '#17181c', bar: '#1c1e22', panel: '#24262b', line: '#33363c', ink: '#f2f0ec', dim: '#9a9488', gold: '#c9a227' };

/**
 * m.6 Treasury — fullscreen dedicirani ekran (preko cijelog BIS shell-a). Objedinjuje bivši
 * CF modul + Likvidaturu (m.21.5) u jedan tamni radni prostor: Pregled (cockpit), Plaćanja
 * (podtabovi Odabir URA i Nalozi), Ispravak (superadmin stanje). Bez BIS tabova i lista.
 * Stari kljuc 'nalozi' ostaje valjan i otvara Placanja > Nalozi.
 */
export function TreasuryModule({ payload, filters, foundation, onChange, onExit, token, user, locale = 'hr', embedded }: {
    payload: CashflowPayload | null;
    filters: Filters;
    foundation: FoundationPayload;
    onChange: (patch: Partial<Filters>) => void;
    onExit: () => void;
    token: string;
    user: UserLike;
    locale?: 'hr' | 'en';
    embedded?: boolean;
}) {
    const [tab, setTabRaw] = useState<TreasuryTab>('pregled');
    const [paySub, setPaySub] = useState<PaymentsSubTab>('odabir');
    // 'nalozi' vise nije glavni tab: mapira se na Placanja + podtab Nalozi.
    const setTab = (key: TreasuryTab) => {
        if (key === 'nalozi') { setPaySub('nalozi'); setTabRaw('placanja'); return; }
        setTabRaw(key);
    };
    const [sigCount, setSigCount] = useState(0);
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const companies = foundation.companies;
    const selectedCompany = filters.companyIds.length === 1 ? String(filters.companyIds[0]) : '';

    React.useEffect(() => {
        if (!token) return;
        fetch('/api/payment-selections/pending-second-signature', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : null).then((d) => { if (d) setSigCount(d.count ?? 0); }).catch(() => undefined);
    }, [token]);

    const filterBar = (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={selectedCompany} onChange={(e) => onChange({ companyIds: e.target.value ? [Number(e.target.value)] : [] })}
                style={selStyle}>
                <option value="">{t('Sve tvrtke', 'All companies')}</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
            </select>
            <DateInput value={filters.dateFrom} onChange={(iso) => { if (iso) onChange({ dateFrom: iso }); }} style={{ ...selStyle, width: 108 }} />
            <span style={{ color: T.dim }}>→</span>
            <DateInput value={filters.dateTo} onChange={(iso) => { if (iso) onChange({ dateTo: iso }); }} style={{ ...selStyle, width: 108 }} />
        </div>
    );

    const tabs: { key: TreasuryTab; label: string }[] = [
        { key: 'pregled', label: t('Pregled', 'Overview') },
        { key: 'placanja', label: t('Plaćanja', 'Payments') },
        { key: 'priljevi', label: t('Priljevi', 'Inflows') },
        { key: 'racuni', label: t('URA', 'URA') },
        { key: 'ira', label: t('IRA', 'IRA') },
        { key: 'potpis', label: '✍ ' + t('2. potpis', '2nd sign') + (sigCount > 0 ? ` (${sigCount})` : '') },
        { key: 'ispravak', label: t('Ispravak', 'Correction') },
        { key: 'ugovori', label: t('Ugovori', 'Contracts') },
        ...(user?.role === 'superadministrator' ? [{ key: 'postavke' as TreasuryTab, label: '⚙ ' + t('Postavke', 'Settings') }] : []),
    ];

    return (
        <div style={embedded
            ? { background: T.bg, color: T.ink, display: 'flex', flexDirection: 'column', minHeight: '100vh', borderRadius: 12, overflow: 'hidden' }
            : { position: 'fixed', inset: 0, zIndex: 4000, background: T.bg, color: T.ink, display: 'flex', flexDirection: 'column', overflow: 'hidden' }
        }>
            <style>{`
                .treasury-embed { color: ${T.ink}; }
                .treasury-embed .card, .treasury-embed .nested-card, .treasury-embed .panel { background: ${T.panel} !important; border-color: ${T.line} !important; color: ${T.ink} !important; box-shadow: none !important; }
                .treasury-embed table { color: ${T.ink}; }
                .treasury-embed thead tr, .treasury-embed th { background: transparent !important; color: ${T.gold} !important; border-color: ${T.line} !important; }
                .treasury-embed td, .treasury-embed tr { border-color: ${T.line} !important; }
                .treasury-embed tbody tr:nth-child(odd) { background: rgba(255,255,255,0.015); }
                .treasury-embed .muted, .treasury-embed .eyebrow { color: ${T.dim} !important; }
                .treasury-embed input, .treasury-embed select, .treasury-embed textarea { background: ${T.bg} !important; color: ${T.ink} !important; border: 1px solid ${T.line} !important; }
                .treasury-embed h1, .treasury-embed h2, .treasury-embed h3, .treasury-embed h4 { color: ${T.ink} !important; }
                .treasury-embed code { background: ${T.bg} !important; color: ${T.dim} !important; }
            `}</style>
            {/* top bar */}
            <div style={{ background: T.bar, borderBottom: `1px solid ${T.line}`, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 16 }}>
                    <span style={{ color: T.gold }}>◆</span> Auro Domus <span style={{ color: T.dim, fontWeight: 400 }}>· Treasury</span>
                </div>
                <div style={{ display: 'flex', gap: 2, marginLeft: 8 }}>
                    {tabs.map((x) => (
                        <button key={x.key} onClick={() => setTab(x.key)}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px 14px', fontSize: 14, fontWeight: tab === x.key ? 700 : 500, color: tab === x.key ? T.ink : T.dim, borderBottom: tab === x.key ? `2px solid ${T.gold}` : '2px solid transparent' }}>
                            {x.label}
                        </button>
                    ))}
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 14, alignItems: 'center' }}>
                    {filterBar}
                    <button onClick={onExit} title={t('Zatvori', 'Close')}
                        style={{ background: 'transparent', border: `1px solid ${T.line}`, color: T.dim, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>
                        ✕ {t('Izlaz', 'Exit')}
                    </button>
                </div>
            </div>

            {/* body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                {!payload && tab === 'pregled' ? <div style={{ color: T.dim, padding: 40, textAlign: 'center' }}>{t('Učitavam cashflow…', 'Loading cashflow…')}</div> : null}
                {tab === 'pregled' && payload ? <><ForecastSourcesPanel token={token} locale={locale} onSaved={() => onChange({})} /><CockpitFull payload={payload} token={token} locale={locale} /></> : null}
                {tab === 'placanja' ? (
                    <>
                        <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
                            {([['odabir', t('Odabir', 'Selection')], ['nalozi', t('Nalozi', 'Batches')]] as [PaymentsSubTab, string][]).map(([k, label]) => (
                                <button key={k} onClick={() => setPaySub(k)}
                                    style={{ background: paySub === k ? T.panel : 'transparent', border: `1px solid ${paySub === k ? T.gold : T.line}`, color: paySub === k ? T.ink : T.dim, borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontSize: 13, fontWeight: paySub === k ? 700 : 500 }}>
                                    {label}
                                </button>
                            ))}
                        </div>
                        {paySub === 'odabir' ? <div className="treasury-embed"><PaymentSelectionModule token={token} locale={locale} onBack={onExit} embedded companyId={selectedCompany} /></div> : null}
                        {paySub === 'nalozi' ? <div className="treasury-embed"><PaymentBatchesModule token={token} locale={locale} onBack={onExit} embedded /></div> : null}
                    </>
                ) : null}
                {tab === 'priljevi' ? <InflowsTab token={token} locale={locale} companyId={selectedCompany} /> : null}
                {tab === 'racuni' ? <UraInvoicesTab token={token} locale={locale} companyId={selectedCompany} /> : null}
                {tab === 'ira' ? <IraInvoicesTab token={token} locale={locale} companyId={selectedCompany} /> : null}
                {tab === 'potpis' ? <SecondSignatureTab token={token} locale={locale} onCount={setSigCount} /> : null}
                {tab === 'ispravak' ? <div style={{ maxWidth: 1100 }}><BalanceCorrectionPanel token={token} onSaved={() => onChange({})} /></div> : null}
                {tab === 'ugovori' ? <div className="treasury-embed"><ContractsModule token={token} locale={locale} onBack={onExit} embedded companyId={selectedCompany} /></div> : null}
                {tab === 'postavke' ? <><ThresholdsTab token={token} locale={locale} onSaved={() => onChange({})} /><ForecastSourcesPanel token={token} locale={locale} onSaved={() => onChange({})} /></> : null}
            </div>
        </div>
    );
}

/** Cockpit razvučen na PUNU širinu ekrana (bez max-width ograničenja) + dnevni tablični pregled ispod. */
function CockpitFull({ payload, token, locale }: { payload: CashflowPayload; token: string; locale: 'hr' | 'en' }) {
    return (
        <div style={{ width: '100%' }}>
            <CashflowCockpit payload={payload} />
            <DailyGridPanel token={token} locale={locale} />
            <ManualCfPanel token={token} locale={locale} />
        </div>
    );
}

// ── Ručni CF unosi — korekcije projekcije (NE ulaze u Plaćanja/likvidaturu) ──
type ManualCfRow = { id: number; date: string; direction: 'inflow' | 'outflow'; amount: number; label: string | null; note: string | null; company_code: string | null };

function ManualCfPanel({ token, locale }: { token: string; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<ManualCfRow[] | null>(null);
    const [gated, setGated] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [companies, setCompanies] = useState<{ id: number; code: string }[]>([]);
    const [companyId, setCompanyId] = useState('');
    const [date, setDate] = useState('');
    const [direction, setDirection] = useState<'inflow' | 'outflow'>('outflow');
    const [amount, setAmount] = useState('');
    const [label, setLabel] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');

    const load = React.useCallback(() => {
        fetch('/api/cashflow/manual-items', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => { if (r.status === 403) { setGated(true); return null; } return r.ok ? r.json() : null; })
            .then((d) => { if (d) setRows(d.rows ?? []); })
            .catch(() => setRows([]));
        fetch('/api/ura-invoices?per_page=25&page=1', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : null).then((d) => { if (d?.companies) setCompanies(d.companies); }).catch(() => undefined);
    }, [token]);
    React.useEffect(() => { load(); }, [load]);

    const save = async () => {
        const amt = Number(String(amount).replace(',', '.'));
        if (!companyId || !date || !label.trim() || !amt || Number.isNaN(amt) || amt <= 0) { setMsg(t('Popuni tvrtku, datum, iznos (>0) i opis.', 'Fill company, date, amount (>0) and label.')); return; }
        setBusy(true); setMsg('');
        try {
            const r = await fetch('/api/cashflow/manual-items', {
                method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ company_id: Number(companyId), date, direction, amount: amt, label: label.trim(), note: note.trim() || null }),
            });
            if (!r.ok) { const e = await r.json().catch(() => ({})); setMsg(e.message ?? 'Greška'); setBusy(false); return; }
            setShowForm(false); setAmount(''); setLabel(''); setNote(''); setDate('');
            load();
        } catch { setMsg('Greška'); }
        setBusy(false);
    };
    const del = async (id: number) => {
        if (!window.confirm(t('Obrisati ručni unos?', 'Delete manual entry?'))) return;
        try { await fetch(`/api/cashflow/manual-items/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }); load(); } catch { /* */ }
    };

    if (gated) return null;
    const fmt = (n: number) => new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' €';
    const fmtD = (d: string) => d.slice(0, 10).split('-').reverse().join('.') + '.';
    const fieldS: React.CSSProperties = { ...selStyle, width: '100%', boxSizing: 'border-box' };
    return (
        <div style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 10, padding: 12, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{t('Ručni CF unosi', 'Manual CF entries')}</div>
                <span style={{ fontSize: 11, color: T.dim }}>{t('korigiraju samo projekciju — NE ulaze u Plaćanja', 'projection corrections only — never enter Payments')}</span>
                <button type="button" onClick={() => { setShowForm((v) => !v); setMsg(''); }} style={{ ...gridBtn, marginLeft: 'auto', borderColor: T.gold, color: T.gold, fontWeight: 700 }}>+ {t('Ručni unos', 'Manual entry')}</button>
            </div>
            {showForm ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 10, padding: 10, border: `1px solid ${T.line}`, borderRadius: 8 }}>
                    <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} style={fieldS}>
                        <option value="">{t('Tvrtka *', 'Company *')}</option>
                        {companies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                    </select>
                    <DateInput value={date} onChange={setDate} style={fieldS} />
                    <select value={direction} onChange={(e) => setDirection(e.target.value as 'inflow' | 'outflow')} style={fieldS}>
                        <option value="outflow">− {t('Odljev', 'Outflow')}</option>
                        <option value="inflow">+ {t('Priljev', 'Inflow')}</option>
                    </select>
                    <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t('Iznos (€) *', 'Amount *')} style={{ ...fieldS, textAlign: 'right' }} />
                    <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('Opis *', 'Label *')} style={fieldS} />
                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('Bilješka', 'Note')} style={fieldS} />
                    <button type="button" disabled={busy} onClick={() => void save()} style={{ background: '#3fae6b', color: '#fff', border: 0, borderRadius: 7, padding: '7px 14px', cursor: 'pointer', fontWeight: 700 }}>{busy ? '…' : t('Spremi', 'Save')}</button>
                    {msg ? <span style={{ color: '#c05a5a', fontSize: 12, alignSelf: 'center', gridColumn: '1 / -1' }}>{msg}</span> : null}
                </div>
            ) : null}
            {rows === null ? <div style={{ color: T.dim, fontSize: 12 }}>{t('Učitavam…', 'Loading…')}</div> : rows.length === 0 ? (
                <div style={{ color: T.dim, fontSize: 12 }}>{t('Nema ručnih unosa.', 'No manual entries.')}</div>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead><tr style={{ textAlign: 'left', color: T.dim, fontSize: 10.5 }}>
                        <th style={{ padding: '4px 8px' }}>{t('Datum', 'Date')}</th><th style={{ padding: '4px 8px' }}>{t('Firma', 'Co.')}</th>
                        <th style={{ padding: '4px 8px' }}>{t('Opis', 'Label')}</th><th style={{ padding: '4px 8px', textAlign: 'right' }}>{t('Iznos', 'Amount')}</th><th></th>
                    </tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} style={{ borderTop: `1px solid ${T.line}` }}>
                                <td style={{ padding: '5px 8px', whiteSpace: 'nowrap', color: T.dim }}>{fmtD(r.date)}</td>
                                <td style={{ padding: '5px 8px' }}>{r.company_code ?? '—'}</td>
                                <td style={{ padding: '5px 8px' }} title={r.note ?? ''}>{r.label ?? '—'}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600, color: r.direction === 'inflow' ? '#3fae6b' : '#c05a5a', whiteSpace: 'nowrap' }}>
                                    {r.direction === 'inflow' ? '+' : '−'}{fmt(r.amount)}
                                </td>
                                <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                                    <button type="button" onClick={() => void del(r.id)} title={t('Obriši', 'Delete')} style={{ background: 'transparent', border: 'none', color: '#c05a5a', cursor: 'pointer' }}>🗑</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ── Dnevni tablični pregled per company (Valerija) — povijest + 15d projekcija + export ──
type GridFlowRow = { company_code: string; bank: string; kind: string; label: string; highlight?: string; values: Record<string, number> };
type GridPayload = {
    dates: { date: string; proj: boolean }[];
    rows: { company_code: string; company_name: string; values: Record<string, number>; is_total?: boolean; is_cash?: boolean }[];
    as_of: string;
    flow?: {
        dates: string[];
        rows: GridFlowRow[];
        balances?: { company_code: string; bank: string; opening: Record<string, number>; closing: Record<string, number> }[];
        company_totals?: GridCompanyTotal[];
    };
    source_lines?: { source_type: string; label: string; values: Record<string, number> }[];
};
type GridCompanyTotal = { company_code: string; opening: Record<string, number>; closing: Record<string, number> } & Record<'real_inflow' | 'transfer_inflow' | 'real_outflow' | 'transfer_outflow', Record<string, number>>;
type FlowLine = GridFlowRow & { balance?: boolean };

function DailyGridPanel({ token, locale }: { token: string; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<GridPayload | null>(null);
    const [err, setErr] = useState('');
    React.useEffect(() => {
        fetch('/api/cashflow/daily-grid', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then((d) => setData(d))
            .catch((e) => setErr(e instanceof Error ? e.message : 'Greška'));
    }, [token]);

    const dl = async (format: 'xlsx' | 'pdf') => {
        try {
            const r = await fetch(`/api/cashflow/daily-grid/export?format=${format}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `cf_dnevni_pregled.${format}`; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { setErr(e instanceof Error ? e.message : 'Greška'); }
    };

    const fmt0 = (n: number) => new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(n);
    const fmt2 = (n: number) => new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    const fmtD = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.`;
    if (err) return <div style={{ color: '#c05a5a', padding: 12 }}>{t('Dnevni pregled nedostupan', 'Daily grid unavailable')}: {err}</div>;
    if (!data) return <div style={{ color: T.dim, padding: 12 }}>{t('Učitavam dnevni pregled…', 'Loading daily grid…')}</div>;
    const flowDates = data.flow?.dates ?? [];
    // Blokovi po tvrtki: svaka banka (pocetno, promet, zavrsno), pa zbroj tvrtke.
    const flowBlocks = (() => {
        const byKey: Record<string, GridFlowRow[]> = {};
        (data.flow?.rows ?? []).forEach((r) => {
            const k = `${r.company_code}|${r.bank}`;
            if (!byKey[k]) byKey[k] = [];
            byKey[k].push(r);
        });
        const balByKey: Record<string, { opening: Record<string, number>; closing: Record<string, number> }> = {};
        (data.flow?.balances ?? []).forEach((b) => { balByKey[`${b.company_code}|${b.bank}`] = b; });
        const keys = Array.from(new Set([...Object.keys(byKey), ...Object.keys(balByKey)])).sort();
        const totals: Record<string, GridCompanyTotal> = {};
        (data.flow?.company_totals ?? []).forEach((c) => { totals[c.company_code] = c; });
        const blocks: { key: string; title: string; total: boolean; lines: FlowLine[] }[] = [];
        const pushTotal = (cc: string) => {
            const c = totals[cc];
            if (!c) return;
            const mk = (kind: string, label: string, values: Record<string, number>, extra: Partial<FlowLine> = {}): FlowLine => ({ company_code: cc, bank: '', kind, label, values, ...extra });
            blocks.push({ key: `${cc}|__total`, title: `${cc} · ${t('ukupno', 'total')}`, total: true, lines: [
                mk('opening', t('Početno stanje', 'Opening balance'), c.opening, { balance: true }),
                mk('real_inflow', t('Stvarni priliv', 'Real inflow'), c.real_inflow),
                mk('transfer_inflow', t('Priliv prijenosi', 'Transfer inflow'), c.transfer_inflow, { highlight: 'green' }),
                mk('real_outflow', t('Stvarni odliv', 'Real outflow'), c.real_outflow),
                mk('transfer_outflow', t('Odliv prijenosi', 'Transfer outflow'), c.transfer_outflow, { highlight: 'green' }),
                mk('closing', t('Završno stanje', 'Closing balance'), c.closing, { balance: true }),
            ] });
        };
        keys.forEach((k, i) => {
            const [cc, bank] = k.split('|');
            const bal = balByKey[k];
            const lines: FlowLine[] = [];
            if (bal) lines.push({ company_code: cc, bank, kind: 'opening', label: t('Početno stanje', 'Opening balance'), values: bal.opening, balance: true });
            lines.push(...(byKey[k] ?? []));
            if (bal) lines.push({ company_code: cc, bank, kind: 'closing', label: t('Završno stanje', 'Closing balance'), values: bal.closing, balance: true });
            blocks.push({ key: k, title: `${cc} · ${bank}`, total: false, lines });
            const next = keys[i + 1];
            if (!next || next.split('|')[0] !== cc) pushTotal(cc);
        });
        return blocks;
    })();

    return (
        <div style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 10, padding: 12, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{t('Dnevni pregled stanja po tvrtkama', 'Daily balances by company')}</div>
                <span style={{ fontSize: 11, color: T.dim }}>{t('povijest iz izvoda · desno od danas = projekcija', 'history from statements · right of today = projection')}</span>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    <button type="button" onClick={() => void dl('xlsx')} style={gridBtn}>⬇ XLS</button>
                    <button type="button" onClick={() => void dl('pdf')} style={gridBtn}>⬇ PDF</button>
                </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 11.5, whiteSpace: 'nowrap', width: '100%' }}>
                    <thead>
                        <tr style={{ color: T.dim, fontSize: 10 }}>
                            <th style={{ textAlign: 'left', padding: '4px 8px', position: 'sticky', left: 0, background: T.panel }}>{t('Tvrtka', 'Company')}</th>
                            {data.dates.map((d) => (
                                <th key={d.date} style={{ textAlign: 'right', padding: '4px 6px', fontStyle: d.proj ? 'italic' : 'normal', color: d.proj ? T.gold : T.dim, borderLeft: d.proj && !data.dates[data.dates.indexOf(d) - 1]?.proj ? `2px solid ${T.gold}` : undefined }}>
                                    {fmtD(d.date)}{d.proj ? ' P' : ''}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.map((r, i) => (
                            <tr key={r.company_code} style={{ borderTop: r.is_cash && !r.is_total && !data.rows[i - 1]?.is_cash ? `2px solid ${T.line}` : `1px solid ${T.line}`, fontWeight: r.is_total ? 700 : 400, background: r.is_total ? 'rgba(201,162,39,0.08)' : undefined }}>
                                <td style={{ padding: '4px 8px', position: 'sticky', left: 0, background: r.is_total ? '#2b2820' : T.panel, fontWeight: 600, color: r.is_cash && !r.is_total ? T.dim : undefined }} title={r.company_name}>{r.company_code}</td>
                                {data.dates.map((d) => {
                                    const v = r.values[d.date];
                                    return (
                                        <td key={d.date} style={{ textAlign: 'right', padding: '4px 6px', color: v == null ? T.dim : v < 0 ? '#c05a5a' : d.proj ? T.gold : T.ink, fontStyle: d.proj ? 'italic' : 'normal' }}>
                                            {v != null ? fmt0(v) : '—'}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {(data.source_lines ?? []).length > 0 ? (
                            <tr style={{ borderTop: `2px solid ${T.line}` }}>
                                <td colSpan={data.dates.length + 1} style={{ padding: '6px 8px 2px', fontSize: 10, color: T.dim, position: 'sticky', left: 0 }}>
                                    {t('Projekcija po izvoru (neto dnevno, sve tvrtke)', 'Projection by source (daily net, all companies)')}
                                </td>
                            </tr>
                        ) : null}
                        {(data.source_lines ?? []).map((l) => (
                            <tr key={`src-${l.source_type}`} style={{ borderTop: `1px solid ${T.line}` }}>
                                <td style={{ padding: '3px 8px', position: 'sticky', left: 0, background: T.panel, color: T.dim }} title={l.source_type}>{l.label}</td>
                                {data.dates.map((d) => {
                                    const v = d.proj ? l.values[d.date] : undefined;
                                    return (
                                        <td key={d.date} style={{ textAlign: 'right', padding: '3px 6px', fontStyle: 'italic', color: v == null || v === 0 ? T.dim : v < 0 ? '#c05a5a' : '#3fae6b' }}>
                                            {v == null ? '' : v === 0 ? '·' : fmt0(v)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {flowDates.length > 0 ? (
                <div style={{ marginTop: 14, borderTop: `1px solid ${T.line}`, paddingTop: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t('Dnevni priljev/odljev — stvarno vs prijenosi', 'Daily in/out — real vs transfers')}</div>
                    <div style={{ fontSize: 11, color: T.dim, marginBottom: 8 }}>{t('Zeleno = prijenosi žr↔žr (punjenje podračuna, oročenje…). Početno/završno = saldo računa tvrtke u banci; zlatni blok = zbroj tvrtke.', 'Green = inter-account transfers. Opening/closing = company balance at the bank; gold block = company total.')}</div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: 11, whiteSpace: 'nowrap', width: '100%' }}>
                            <thead>
                                <tr style={{ color: T.dim, fontSize: 10 }}>
                                    <th style={{ textAlign: 'left', padding: '4px 8px', position: 'sticky', left: 0, background: T.panel }}>{t('Firma · banka', 'Co. · bank')}</th>
                                    <th style={{ textAlign: 'left', padding: '4px 8px' }}>{t('Stavka', 'Line')}</th>
                                    {flowDates.map((d) => (
                                        <th key={d} style={{ textAlign: 'right', padding: '4px 6px' }}>{fmtD(d)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {flowBlocks.map((b) => (
                                    b.lines.map((r, idx) => {
                                        const rowBg = b.total ? 'rgba(201,162,39,0.08)' : r.highlight === 'green' ? 'rgba(63,174,107,0.10)' : undefined;
                                        const stickyBg = b.total ? '#2b2820' : r.highlight === 'green' ? '#1e2a22' : T.panel;
                                        return (
                                            <tr key={`${b.key}-${r.kind}`} style={{ borderTop: idx === 0 ? `1px solid ${T.line}` : undefined, background: rowBg, fontWeight: b.total || r.balance ? 700 : 400 }}>
                                                <td style={{ padding: '4px 8px', position: 'sticky', left: 0, background: stickyBg, fontWeight: idx === 0 ? 600 : 400, color: idx === 0 ? T.ink : T.dim }}>
                                                    {idx === 0 ? b.title : ''}
                                                </td>
                                                <td style={{ padding: '4px 8px', color: r.highlight === 'green' ? '#3fae6b' : r.balance ? T.ink : T.dim }}>{r.label}</td>
                                                {flowDates.map((d) => {
                                                    const v = r.values[d];
                                                    const color = v == null || (!r.balance && v === 0) ? T.dim
                                                        : r.balance ? (v < 0 ? '#c05a5a' : T.ink)
                                                        : r.highlight === 'green' ? '#3fae6b' : (r.kind.includes('outflow') ? '#c05a5a' : T.ink);
                                                    const show = v != null && (r.balance || Math.abs(v) >= 0.005);
                                                    return (
                                                        <td key={d} style={{ textAlign: 'right', padding: '4px 6px', color }}>
                                                            {show ? fmt2(v) : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
const gridBtn: React.CSSProperties = { background: 'transparent', border: `1px solid ${T.line}`, color: T.dim, borderRadius: 7, padding: '4px 10px', cursor: 'pointer', fontSize: 12 };
const tagChip: React.CSSProperties = { background: 'transparent', border: `1px solid ${T.line}`, borderRadius: 14, padding: '3px 10px', fontSize: 11.5, cursor: 'pointer' };

type PendingSig = { id: number; document_number: string; due_date: string | null; amount: number; company_code: string; counterparty_name: string | null; first_signer: string | null; first_signer_id: number | null };
type Manager = { id: number; name: string };
type Nudge = { id: number; note: string | null; from_name: string | null };
const S = { bg: '#17181c', panel: '#24262b', line: '#33363c', ink: '#f2f0ec', dim: '#9a9488', gold: '#c9a227', green: '#3fae6b', red: '#c05a5a' };
const sFmt = (n: number) => new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(n) + ' €';
const sortH: React.CSSProperties = { padding: '8px 10px', cursor: 'pointer', userSelect: 'none' };

/** Tab "2. potpis" — drugi zadavatelj potpisuje račune iznad praga + nudge + primljeni pozivi. */
function SecondSignatureTab({ token, onCount, locale }: { token: string; onCount: (n: number) => void; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<PendingSig[]>([]);
    const [managers, setManagers] = useState<Manager[]>([]);
    const [nudges, setNudges] = useState<Nudge[]>([]);
    const [threshold, setThreshold] = useState<number | null>(null);
    const [isSigner, setIsSigner] = useState(true);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [sortKey, setSortKey] = useState<'company_code' | 'counterparty_name' | 'document_number' | 'amount' | 'first_signer'>('amount');
    const [sortDir, setSortDir] = useState<1 | -1>(-1);
    const sortBy = (k: typeof sortKey) => { if (sortKey === k) { setSortDir((d) => (d === 1 ? -1 : 1)); } else { setSortKey(k); setSortDir(k === 'amount' ? -1 : 1); } };
    const arrow = (k: typeof sortKey) => sortKey === k ? (sortDir === 1 ? ' ▲' : ' ▼') : '';

    const load = React.useCallback(async () => {
        try {
            const [p, n] = await Promise.all([
                fetch('/api/payment-selections/pending-second-signature', { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
                fetch('/api/payment-selections/my-nudges', { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
            ]);
            setRows(p.rows ?? []); setManagers(p.managers ?? []); setThreshold(p.threshold ?? null); setIsSigner(p.is_signer ?? false);
            onCount(p.count ?? 0); setNudges(n.nudges ?? []);
        } catch { /* ignore */ }
    }, [token, onCount]);
    React.useEffect(() => { void load(); }, [load]);

    const sign = async (id: number) => {
        setBusy(true); setMsg('');
        try { const r = await fetch(`/api/payment-selections/${id}/second-sign`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }); if (!r.ok) { const e = await r.json().catch(() => ({})); setMsg(e.message ?? 'Greška'); } await load(); } catch { setMsg('Greška'); } setBusy(false);
    };
    const nudge = async (uid: number) => {
        try { await fetch('/api/payment-selections/nudge', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ to_user_id: uid }) }); setMsg(t('🔔 Poziv poslan.', '🔔 Nudge sent.')); } catch { setMsg('Greška'); }
    };
    const ackNudges = async () => { try { await fetch('/api/payment-selections/my-nudges?ack=1', { headers: { Authorization: `Bearer ${token}` } }); setNudges([]); } catch { /* */ } };

    if (!isSigner) return <div style={{ color: S.dim, padding: 24 }}>{t('Nisi zadavatelj — 2. potpis daju određeni korisnici (postavke).', 'You are not an authorizer.')}</div>;

    return (
        <div style={{ maxWidth: 1100, margin: '0 auto', color: S.ink }}>
            {nudges.length > 0 ? (
                <div style={{ background: 'rgba(201,162,39,0.14)', border: `1px solid ${S.gold}`, borderRadius: 10, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>🔔</span>
                    <span style={{ flex: 1 }}>{nudges.map((x) => x.from_name).filter(Boolean).join(', ')} {t('te zove da dođeš potpisati', 'nudged you to sign')} ({nudges.length}).</span>
                    <button onClick={() => void ackNudges()} style={{ background: 'transparent', border: `1px solid ${S.line}`, color: S.dim, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>{t('U redu', 'OK')}</button>
                </div>
            ) : null}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <h3 style={{ margin: 0 }}>✍ {t('Čeka tvoj 2. potpis', 'Awaiting your 2nd signature')} ({rows.length})</h3>
                <span style={{ color: S.dim, fontSize: 12 }}>{t('Prag', 'Threshold')}: {threshold == null ? t('nije postavljen, 2. potpis za svaki račun', 'not set, every invoice needs 2nd signature') : sFmt(threshold)}</span>
                {msg ? <span style={{ marginLeft: 'auto', color: S.gold, fontSize: 13 }}>{msg}</span> : null}
            </div>

            {rows.length === 0 ? <div style={{ color: S.dim, padding: 16 }}>{t('Ništa ne čeka tvoj potpis.', 'Nothing awaits your signature.')}</div> : (
                <div style={{ background: S.panel, border: `1px solid ${S.line}`, borderRadius: 10, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead><tr style={{ textAlign: 'left', color: S.dim, fontSize: 11 }}>
                            <th style={sortH} onClick={() => sortBy('company_code')}>{t('Tvrtka', 'Co.')}{arrow('company_code')}</th><th style={sortH} onClick={() => sortBy('counterparty_name')}>{t('Dobavljač', 'Supplier')}{arrow('counterparty_name')}</th>
                            <th style={sortH} onClick={() => sortBy('document_number')}>{t('Dokument', 'Doc')}{arrow('document_number')}</th><th style={{ ...sortH, textAlign: 'right' }} onClick={() => sortBy('amount')}>{t('Iznos', 'Amount')}{arrow('amount')}</th>
                            <th style={sortH} onClick={() => sortBy('first_signer')}>{t('1. potpis', '1st sign')}{arrow('first_signer')}</th><th style={{ padding: '8px 10px' }}></th>
                        </tr></thead>
                        <tbody>
                            {[...rows].sort((a, b) => {
                                const av = a[sortKey], bv = b[sortKey];
                                if (typeof av === 'number' || typeof bv === 'number') return sortDir * ((Number(av ?? 0)) - (Number(bv ?? 0)));
                                return sortDir * String(av ?? '').localeCompare(String(bv ?? ''));
                            }).map((r) => (
                                <tr key={r.id} style={{ borderTop: `1px solid ${S.line}` }}>
                                    <td style={{ padding: '8px 10px' }}>{r.company_code}</td>
                                    <td style={{ padding: '8px 10px' }}>{r.counterparty_name ?? '—'}</td>
                                    <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11, color: S.dim }}>{r.document_number}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>{sFmt(r.amount)}</td>
                                    <td style={{ padding: '8px 10px', color: S.dim }}>{r.first_signer ?? '—'}</td>
                                    <td style={{ padding: '8px 10px' }}><button disabled={busy} onClick={() => void sign(r.id)} style={{ background: S.green, color: '#fff', border: 0, borderRadius: 6, padding: '5px 12px', fontWeight: 700, cursor: 'pointer' }}>✍ {t('Potpiši', 'Sign')}</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {managers.length > 0 ? (
                <div style={{ marginTop: 18 }}>
                    <div style={{ fontSize: 13, color: S.dim, marginBottom: 8 }}>🔔 {t('Pozovi drugog zadavatelja da dođe potpisati:', 'Nudge another authorizer to sign:')}</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {managers.map((m) => <button key={m.id} onClick={() => void nudge(m.id)} style={{ background: S.panel, border: `1px solid ${S.line}`, color: S.ink, borderRadius: 20, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>{m.name} 🔔</button>)}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

// ── Tab "Računi" — puni URA registar (bi_ura_invoices; ZASEBAN od payment modula) ──
type UraRow = { id: number; company_code: string; document_number: string | null; invoice_number: string | null; document_date: string | null; supplier_name: string | null; is_person: boolean | null; amount_total: number; paid_faros: number; amount_open: number; status: 'placen' | 'otvoren'; statement_matched: boolean; statement_paid_eur: number | null; statement_date: string | null; tags: { tag: string; source: string; is_primary: boolean }[]; virman?: { batch_id: number; batch_status: string; batch_date: string | null; item_status: string; amount: number; realized: boolean; confirmed: boolean } | null };
type UraPayload = { rows: UraRow[]; total: number; page: number; per_page: number; summary: { count: number; total: number; paid: number; open: number; matched: number; persons: number } | null; companies: { id: number; code: string }[]; tags: { tag: string; n: number; primary: boolean }[] };
type UraGroup = { mt: string | null; count: number; total: number; open: number };

function UraInvoicesTab({ token, locale, companyId = '' }: { token: string; locale: 'hr' | 'en'; companyId?: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<UraPayload | null>(null);
    const [err, setErr] = useState('');
    // Odabir tvrtke u zaglavlju Treasuryja vrijedi i ovdje; lokalni izbornik ga moze promijeniti.
    const [company, setCompany] = useState(companyId);
    useEffect(() => { setCompany(companyId); }, [companyId]);
    const [status, setStatus] = useState('');
    const [party, setParty] = useState('');
    const [region, setRegion] = useState('');
    const [b2, setB2] = useState('');
    const [selTags, setSelTags] = useState<string[]>([]);
    const [dateFrom, setDateFrom] = useState(`${new Date().getFullYear()}-01-01`);
    const [dateTo, setDateTo] = useState('');
    const [q, setQ] = useState('');
    const [qLive, setQLive] = useState('');
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState('document_date');
    const [dir, setDir] = useState<'asc' | 'desc'>('desc');
    const [groupByMt, setGroupByMt] = useState(false);
    const [tagsOpen, setTagsOpen] = useState(false);
    const [groups, setGroups] = useState<UraGroup[] | null>(null);
    const [expanded, setExpanded] = useState<Record<string, UraRow[] | 'loading'>>({});
    const sortBy = (k: string) => { if (sort === k) { setDir((d) => (d === 'asc' ? 'desc' : 'asc')); } else { setSort(k); setDir(k === 'supplier_name' || k === 'company' ? 'asc' : 'desc'); } setPage(1); };
    const arrow = (k: string) => sort === k ? (dir === 'asc' ? ' ▲' : ' ▼') : '';
    const toggleTag = (tag: string) => { setSelTags((p) => p.includes(tag) ? p.filter((x) => x !== tag) : [...p, tag]); setPage(1); setExpanded({}); };

    const filterParams = React.useCallback((extra?: Record<string, string>) => {
        const p = new URLSearchParams(extra ?? {});
        if (company) p.set('company_id', company);
        if (status) p.set('status', status);
        if (party) p.set('party', party);
        if (region) p.set('region', region);
        if (b2) p.set('b2', b2);
        if (selTags.length) p.set('tags', selTags.join(','));
        if (dateFrom) p.set('date_from', dateFrom);
        if (dateTo) p.set('date_to', dateTo);
        if (q) p.set('q', q);
        return p;
    }, [company, status, party, region, b2, selTags, dateFrom, dateTo, q]);

    React.useEffect(() => { const h = setTimeout(() => { setQ(qLive); setPage(1); }, 350); return () => clearTimeout(h); }, [qLive]);
    React.useEffect(() => {
        const p = filterParams({ page: String(page), sort, dir, per_page: '100' });
        fetch(`/api/ura-invoices?${p.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(async (r) => { if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.message ?? `HTTP ${r.status}`); } return r.json(); })
            .then((d) => { setData(d); setErr(''); })
            .catch((e) => setErr(e instanceof Error ? e.message : 'Greška'));
    }, [token, filterParams, page, sort, dir]);
    React.useEffect(() => {
        if (!groupByMt) return;
        setGroups(null); setExpanded({});
        fetch(`/api/ura-invoices/grouped?${filterParams().toString()}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then((d) => setGroups(d.groups ?? []))
            .catch((e) => setErr(e instanceof Error ? e.message : 'Greška'));
    }, [token, groupByMt, filterParams]);

    const expand = (mt: string | null) => {
        const key = mt ?? '__none__';
        if (expanded[key]) { setExpanded((p) => { const n = { ...p }; delete n[key]; return n; }); return; }
        setExpanded((p) => ({ ...p, [key]: 'loading' }));
        const p = filterParams({ page: '1', per_page: '200', sort: 'amount_total', dir: 'desc', primary_tag: mt ?? '__none__' });
        fetch(`/api/ura-invoices?${p.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then((d) => setExpanded((prev) => ({ ...prev, [key]: d.rows ?? [] })))
            .catch(() => setExpanded((prev) => { const n = { ...prev }; delete n[key]; return n; }));
    };

    const dl = async (format: 'xlsx' | 'pdf') => {
        try {
            const p = filterParams({ format, sort, dir });
            if (groupByMt) p.set('group_by', 'mt'); // export prati prikaz: grupirano → grupirani export
            const r = await fetch(`/api/ura-invoices/export?${p.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `ura_${groupByMt ? 'grupirano' : 'registar'}.${format}`; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { setErr(e instanceof Error ? e.message : 'Greška'); }
    };

    const fmt = (n: number) => new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' €';
    const fmtD = (d: string | null) => d ? d.slice(0, 10).split('-').reverse().join('.') + '.' : '—';
    const pages = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1;
    const th: React.CSSProperties = { padding: '8px 10px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
    const td: React.CSSProperties = { padding: '7px 10px' };
    const STATUS_UI: Record<string, { hr: string; bg: string; fg: string }> = {
        placen: { hr: 'PLAĆEN', bg: 'rgba(63,174,107,0.18)', fg: '#3fae6b' },
        otvoren: { hr: 'OTVOREN', bg: 'rgba(192,90,90,0.18)', fg: '#c05a5a' },
    };

    const invoiceTable = (rows: UraRow[], compact = false) => (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: compact ? 11.5 : 12.5 }}>
            {!compact ? (
                <thead><tr style={{ textAlign: 'left', color: T.dim, fontSize: 11, position: 'sticky', top: 0, background: T.panel, zIndex: 1 }}>
                    <th style={th} onClick={() => sortBy('company')}>{t('Firma', 'Co.')}{arrow('company')}</th>
                    <th style={th} onClick={() => sortBy('document_date')}>{t('Datum', 'Date')}{arrow('document_date')}</th>
                    <th style={th} onClick={() => sortBy('supplier_name')}>{t('Dobavljač', 'Supplier')}{arrow('supplier_name')}</th>
                    <th style={th} onClick={() => sortBy('document_number')}>{t('Dokument', 'Doc')}{arrow('document_number')}</th>
                    <th style={{ ...th, cursor: 'default' }}>MT (P&L)</th>
                    <th style={{ ...th, textAlign: 'right' }} onClick={() => sortBy('amount_total')}>{t('Iznos', 'Amount')}{arrow('amount_total')}</th>
                    <th style={{ ...th, textAlign: 'right' }} onClick={() => sortBy('amount_open')}>{t('Otvoreno', 'Open')}{arrow('amount_open')}</th>
                    <th style={{ ...th, cursor: 'default' }}>Status</th>
                    <th style={{ ...th, cursor: 'default' }}>{t('Izvod', 'Stmt')}</th>
                    <th style={{ ...th, cursor: 'default' }} title={t('BIS nalog i virman (BatchVirman)', 'BIS batch and wire')}>{t('Nalog', 'Batch')}</th>
                </tr></thead>
            ) : null}
            <tbody>
                {rows.map((r) => {
                    const su = STATUS_UI[r.status];
                    const primary = r.tags.find((tg) => tg.is_primary);
                    const others = r.tags.filter((tg) => !tg.is_primary);
                    return (
                        <tr key={r.id} style={{ borderTop: `1px solid ${T.line}` }}>
                            <td style={td}>{r.company_code}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap', color: T.dim }}>{fmtD(r.document_date)}</td>
                            <td style={{ ...td, maxWidth: 260 }}>
                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.supplier_name ?? ''}>{r.is_person ? '👤 ' : ''}{r.supplier_name ?? '—'}</div>
                                {others.length > 0 ? (
                                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                                        {others.map((tg) => (
                                            <span key={tg.tag} onClick={() => toggleTag(tg.tag)} title={tg.source === 'auto' ? t('Auto (LVL2)', 'Auto (LVL2)') : t('Ručni tag', 'Manual tag')}
                                                style={{ fontSize: 9.5, border: `1px solid ${T.line}`, color: tg.source === 'auto' ? T.dim : T.gold, borderRadius: 8, padding: '0 6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>{tg.tag}</span>
                                        ))}
                                    </div>
                                ) : null}
                            </td>
                            <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: T.dim, whiteSpace: 'nowrap' }} title={r.invoice_number ?? ''}>{r.document_number ?? '—'}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{primary ? <span onClick={() => toggleTag(primary.tag)} style={{ fontSize: 10.5, border: `1px solid ${T.gold}`, color: T.gold, borderRadius: 8, padding: '1px 8px', cursor: 'pointer', fontWeight: 600 }}>{primary.tag}</span> : <span style={{ color: T.dim }}>—</span>}</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmt(r.amount_total)}</td>
                            <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', color: Math.abs(r.amount_open) >= 0.01 ? '#c05a5a' : T.dim }}>{fmt(r.amount_open)}</td>
                            <td style={td}><span style={{ background: su.bg, color: su.fg, padding: '2px 8px', borderRadius: 5, fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{su.hr}</span></td>
                            <td style={{ ...td, whiteSpace: 'nowrap' }} title={r.statement_matched ? `${t('Po izvodu', 'Per statement')} ${fmtD(r.statement_date)} · ${r.statement_paid_eur != null ? fmt(r.statement_paid_eur) : ''}` : t('Nije upareno s izvodom', 'Not matched')}>
                                {r.statement_matched ? <span style={{ color: T.gold }}>✓ {fmtD(r.statement_date)}</span> : <span style={{ color: T.dim }}>—</span>}
                            </td>
                            <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 11 }} title={r.virman ? `${t('Nalog', 'Batch')} #${r.virman.batch_id} · ${r.virman.batch_status} · ${fmt(r.virman.amount)}` : t('Nije u BIS nalogu', 'Not in a BIS batch')}>
                                {r.virman ? <span style={{ color: r.virman.confirmed ? T.gold : T.ink }}>#{r.virman.batch_id} {fmtD(r.virman.batch_date)}{r.virman.confirmed ? ' ✓' : r.virman.realized ? ' R' : ''}</span> : <span style={{ color: T.dim }}>—</span>}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );

    const grandTotal = groups?.reduce((s, g) => s + g.total, 0) ?? 0;

    return (
        <div style={{ color: T.ink }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>{t('Registar ulaznih računa (URA)', 'Incoming invoices register')}</h3>
                <span style={{ color: T.dim, fontSize: 12 }}>{t('live podaci · status plaćanja je mjerodavan iz modula Plaćanja (sakriveno ne stvara dug)', 'live · payment status from Payments module')}</span>
            </div>
            {/* KPI */}
            {data?.summary ? (
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    {[
                        [t('Računa', 'Invoices'), String(data.summary.count), T.ink],
                        [t('Iznos', 'Total'), fmt(data.summary.total), T.ink],
                        [t('Plaćeno (pretp.)', 'Paid (assumed)'), fmt(data.summary.paid), '#3fae6b'],
                        [t('Otvoreno (Plaćanja)', 'Open (Payments)'), fmt(data.summary.open), '#c05a5a'],
                        [t('Fizičke os.', 'Individuals'), String(data.summary.persons), T.ink],
                        [t('Upareno s izvodom', 'Matched to statement'), `${data.summary.matched}`, T.gold],
                    ].map(([l, v, c], i) => (
                        <div key={i} style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 10, padding: '8px 12px', minWidth: 120, flex: 1 }}>
                            <div style={{ fontSize: 11, color: T.dim }}>{l}</div>
                            <div style={{ fontSize: 17, fontWeight: 700, color: c as string }}>{v}</div>
                        </div>
                    ))}
                </div>
            ) : null}
            {/* filteri */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select value={company} onChange={(e) => { setCompany(e.target.value); setPage(1); setExpanded({}); }} style={selStyle}>
                    <option value="">{t('Sve tvrtke', 'All companies')}</option>
                    {(data?.companies ?? []).map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                </select>
                <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); setExpanded({}); }} style={selStyle}>
                    <option value="">{t('Svi statusi', 'All statuses')}</option>
                    <option value="open">{t('Otvoreni (u Plaćanjima)', 'Open (in Payments)')}</option>
                    <option value="paid">{t('Plaćeni (pretp.)', 'Paid (assumed)')}</option>
                    <option value="izvod">{t('Upareni s izvodom', 'Matched')}</option>
                    <option value="neupareno">{t('Plaćeni bez izvoda', 'Paid, no statement')}</option>
                </select>
                <select value={party} onChange={(e) => { setParty(e.target.value); setPage(1); setExpanded({}); }} style={selStyle}>
                    <option value="">{t('Sve osobe', 'All parties')}</option>
                    <option value="fizicke">{t('Fizičke (otkup)', 'Individuals (buyback)')}</option>
                    <option value="pravne">{t('Pravne osobe', 'Legal entities')}</option>
                </select>
                <select value={region} onChange={(e) => { setRegion(e.target.value); setPage(1); setExpanded({}); }} style={selStyle} title={t('Država iz Farosovog šifrarnika partnera', 'Country from the Faros partner register')}>
                    <option value="">{t('Tuzemni + EU + izvan EU', 'All regions')}</option>
                    <option value="hr">{t('Tuzemni', 'Domestic')}</option>
                    <option value="eu">EU</option>
                    <option value="non_eu">{t('Izvan EU', 'Non-EU')}</option>
                    <option value="unknown">{t('Država nepoznata', 'Country unknown')}</option>
                </select>
                <select value={b2} onChange={(e) => { setB2(e.target.value); setPage(1); setExpanded({}); }} style={selStyle}>
                    <option value="">B2B + B2C</option>
                    <option value="b2b">B2B</option>
                    <option value="b2c">B2C</option>
                </select>
                {(() => {
                    const Y = new Date().getFullYear();
                    const pad = (n: number) => String(n).padStart(2, '0');
                    const now = new Date();
                    const quick: [string, string, string][] = [
                        [t('Ova godina', 'This year'), `${Y}-01-01`, ''],
                        [t('Ovaj mjesec', 'This month'), `${Y}-${pad(now.getMonth() + 1)}-01`, ''],
                        [String(Y - 1), `${Y - 1}-01-01`, `${Y - 1}-12-31`],
                        [t('Sve', 'All'), '', ''],
                    ];
                    return (
                        <span style={{ display: 'inline-flex', gap: 3 }}>
                            {quick.map(([lbl, from, to]) => {
                                const on = dateFrom === from && dateTo === to;
                                return (
                                    <button key={lbl} type="button" onClick={() => { setDateFrom(from); setDateTo(to); setPage(1); setExpanded({}); }}
                                        style={{ background: on ? 'rgba(201,162,39,0.16)' : 'transparent', border: `1px solid ${on ? T.gold : T.line}`, color: on ? T.gold : T.dim, borderRadius: 14, padding: '4px 10px', fontSize: 11.5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                        {lbl}
                                    </button>
                                );
                            })}
                        </span>
                    );
                })()}
                <DateInput value={dateFrom} onChange={(iso) => { setDateFrom(iso); setPage(1); setExpanded({}); }} style={{ ...selStyle, width: 108 }} />
                <span style={{ color: T.dim }}>→</span>
                <DateInput value={dateTo} onChange={(iso) => { setDateTo(iso); setPage(1); setExpanded({}); }} style={{ ...selStyle, width: 108 }} />
                <input value={qLive} onChange={(e) => setQLive(e.target.value)} placeholder={t('Traži dobavljača, dokument, račun…', 'Search…')}
                    style={{ ...selStyle, minWidth: 180 }} />
                <button type="button" onClick={() => setGroupByMt((v) => !v)}
                    style={{ ...selStyle, cursor: 'pointer', borderColor: groupByMt ? T.gold : T.line, color: groupByMt ? T.gold : T.ink, fontWeight: 600 }}>
                    {groupByMt ? '☰ ' : '⊞ '}{t('Grupiraj po MT', 'Group by MT')}
                </button>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button type="button" onClick={() => void dl('xlsx')} style={{ ...selStyle, cursor: 'pointer' }}>⬇ XLS</button>
                    <button type="button" onClick={() => void dl('pdf')} style={{ ...selStyle, cursor: 'pointer' }}>⬇ PDF</button>
                    {!groupByMt ? (
                        <>
                            <span style={{ color: T.dim, fontSize: 12 }}>{data ? `${data.total} · ${page}/${pages}` : '…'}</span>
                            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={{ ...selStyle, cursor: 'pointer' }}>‹</button>
                            <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} style={{ ...selStyle, cursor: 'pointer' }}>›</button>
                        </>
                    ) : null}
                </span>
            </div>
            {err ? <div style={{ background: 'rgba(192,90,90,0.15)', color: '#c05a5a', padding: 10, borderRadius: 8, marginBottom: 8 }}>{err}</div> : null}
            <div style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 10, overflow: 'hidden' }}>
                {/* Filter linija s checkboxima (multi-tag AND) — harmonika, zatvorena inicijalno */}
                {(data?.tags?.length ?? 0) > 0 ? (
                    <div style={{ borderBottom: `1px solid ${T.line}`, background: 'rgba(201,162,39,0.04)' }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '6px 10px', cursor: 'pointer', userSelect: 'none' }} onClick={() => setTagsOpen((o) => !o)}>
                            <span style={{ fontSize: 10.5, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{tagsOpen ? '▾' : '▸'} {t('Tagovi', 'Tags')}</span>
                            {!tagsOpen && selTags.length > 0 ? selTags.map((tg) => (
                                <span key={tg} onClick={(e) => { e.stopPropagation(); toggleTag(tg); }} title={t('Klik = makni filter', 'Click = remove filter')}
                                    style={{ fontSize: 10.5, border: `1px solid ${T.gold}`, color: T.gold, borderRadius: 8, padding: '1px 8px', whiteSpace: 'nowrap' }}>{tg === '__untagged__' ? t('Bez taga', 'Untagged') : tg} ✕</span>
                            )) : null}
                            {!tagsOpen && selTags.length === 0 ? <span style={{ fontSize: 11, color: T.dim, opacity: 0.7 }}>{t('klikni za filtriranje po tagovima', 'click to filter by tags')} ({data?.tags.length})</span> : null}
                            {selTags.length > 0 ? <button type="button" onClick={(e) => { e.stopPropagation(); setSelTags([]); setPage(1); setExpanded({}); }} style={{ background: 'transparent', border: 'none', color: T.gold, cursor: 'pointer', fontSize: 11, marginLeft: 'auto' }}>✕ {t('očisti sve', 'clear all')}</button> : null}
                        </div>
                        {tagsOpen ? (
                            <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', padding: '0 10px 8px' }}>
                                {(data?.tags ?? []).map((tg) => (
                                    <label key={tg.tag} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, padding: '2px 8px', borderRadius: 6, cursor: 'pointer', color: selTags.includes(tg.tag) ? T.gold : T.dim, border: `1px solid ${selTags.includes(tg.tag) ? T.gold : 'transparent'}` }}>
                                        <input type="checkbox" checked={selTags.includes(tg.tag)} onChange={() => toggleTag(tg.tag)} style={{ accentColor: '#c9a227', width: 12, height: 12 }} />
                                        {tg.primary ? <b>{tg.tag}</b> : tg.tag} <span style={{ opacity: 0.55 }}>({tg.n})</span>
                                    </label>
                                ))}
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, padding: '2px 8px', borderRadius: 6, cursor: 'pointer', color: selTags.includes('__untagged__') ? T.gold : T.dim }}>
                                    <input type="checkbox" checked={selTags.includes('__untagged__')} onChange={() => toggleTag('__untagged__')} style={{ accentColor: '#c9a227', width: 12, height: 12 }} />
                                    {t('Bez taga', 'Untagged')}
                                </label>
                            </div>
                        ) : null}
                    </div>
                ) : null}
                <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 360px)', overflowY: 'auto' }}>
                    {!groupByMt ? (
                        <>
                            {invoiceTable(data?.rows ?? [])}
                            {data && data.rows.length === 0 ? <div style={{ color: T.dim, padding: 20, textAlign: 'center' }}>{t('Nema računa za zadane filtere.', 'No invoices for these filters.')}</div> : null}
                        </>
                    ) : (
                        /* Group by MT — drill-down kao u MNG reportu */
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                            <thead><tr style={{ textAlign: 'left', color: T.dim, fontSize: 11, position: 'sticky', top: 0, background: T.panel, zIndex: 1 }}>
                                <th style={{ ...th, cursor: 'default' }}>MT (P&L {t('grupa', 'group')})</th>
                                <th style={{ ...th, cursor: 'default', textAlign: 'right' }}>{t('Računa', 'Invoices')}</th>
                                <th style={{ ...th, cursor: 'default', textAlign: 'right' }}>{t('Iznos', 'Amount')}</th>
                                <th style={{ ...th, cursor: 'default', textAlign: 'right' }}>{t('Udio', 'Share')}</th>
                                <th style={{ ...th, cursor: 'default', textAlign: 'right' }}>{t('Otvoreno (Plaćanja)', 'Open')}</th>
                            </tr></thead>
                            <tbody>
                                {groups === null ? (
                                    <tr><td colSpan={5} style={{ ...td, color: T.dim, textAlign: 'center', padding: 20 }}>{t('Učitavam…', 'Loading…')}</td></tr>
                                ) : groups.map((g) => {
                                    const key = g.mt ?? '__none__';
                                    const exp = expanded[key];
                                    return (
                                        <React.Fragment key={key}>
                                            <tr onClick={() => expand(g.mt)} style={{ borderTop: `1px solid ${T.line}`, cursor: 'pointer', background: exp ? 'rgba(201,162,39,0.08)' : undefined }}>
                                                <td style={{ ...td, fontWeight: 700 }}>{exp ? '▾' : '▸'} {g.mt ?? t('— bez MT taga —', '— no MT —')}</td>
                                                <td style={{ ...td, textAlign: 'right', color: T.dim }}>{g.count}</td>
                                                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(g.total)}</td>
                                                <td style={{ ...td, textAlign: 'right', color: T.dim }}>{grandTotal > 0 ? (100 * g.total / grandTotal).toFixed(1) + '%' : '—'}</td>
                                                <td style={{ ...td, textAlign: 'right', color: Math.abs(g.open) >= 0.01 ? '#c05a5a' : T.dim }}>{fmt(g.open)}</td>
                                            </tr>
                                            {exp === 'loading' ? (
                                                <tr><td colSpan={5} style={{ ...td, color: T.dim, paddingLeft: 30 }}>{t('Učitavam…', 'Loading…')}</td></tr>
                                            ) : Array.isArray(exp) ? (
                                                <tr><td colSpan={5} style={{ padding: '0 0 8px 26px', background: 'rgba(0,0,0,0.12)' }}>
                                                    {invoiceTable(exp, true)}
                                                    {exp.length >= 200 ? <div style={{ color: T.dim, fontSize: 11, padding: '4px 10px' }}>{t('Prikazano prvih 200 po iznosu — za sve koristi filtere ili export.', 'First 200 by amount shown.')}</div> : null}
                                                </td></tr>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                                {groups !== null ? (
                                    <tr style={{ borderTop: `2px solid ${T.gold}`, fontWeight: 700 }}>
                                        <td style={td}>{t('UKUPNO', 'TOTAL')}</td>
                                        <td style={{ ...td, textAlign: 'right' }}>{groups.reduce((s, g) => s + g.count, 0)}</td>
                                        <td style={{ ...td, textAlign: 'right' }}>{fmt(grandTotal)}</td>
                                        <td style={{ ...td, textAlign: 'right' }}>100%</td>
                                        <td style={{ ...td, textAlign: 'right' }}>{fmt(groups.reduce((s, g) => s + g.open, 0))}</td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Tab "IRA" — izlazni računi; građani ANONIMIZIRANI i grupirani kao jedan partner ──
type IraRow = { id: number; company_code: string; invoice_number: string | null; document_date: string | null; customer_name: string | null; is_retail: boolean; doc_prefix: string | null; doc_label: string; category?: string; category_label?: string; amount_eur: number; paid_eur: number; status: string };
type IraGroup = { group: string; label: string; count: number; neto: number; otvoreno: number; is_citizens?: boolean };
type IraPayload = { rows: IraRow[]; total: number; page: number; per_page: number; summary: { count: number; neto: number; prihod: number; tca: number; otkup: number; avans: number; kovnica: number; otvoreno: number } | null; companies: { id: number; code: string }[]; prefixes: { prefix: string; company_code?: string; label: string; hint?: string; neto?: number; n: number }[]; categories?: { key: string; label: string; n: number; neto: number }[] };

function IraInvoicesTab({ token, locale, companyId = '' }: { token: string; locale: 'hr' | 'en'; companyId?: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<IraPayload | null>(null);
    const [err, setErr] = useState('');
    // Odabir tvrtke u zaglavlju Treasuryja vrijedi i ovdje; lokalni izbornik ga moze promijeniti.
    const [company, setCompany] = useState(companyId);
    useEffect(() => { setCompany(companyId); }, [companyId]);
    const [prefix, setPrefix] = useState('');
    const [role, setRole] = useState('');
    const [region, setRegion] = useState('');
    const [b2, setB2] = useState('');
    const [category, setCategory] = useState('');
    const [status, setStatus] = useState('');
    // Default: TEKUĆA GODINA — inače KPI zbraja sve godine (2025+2026) i "prihod" ispadne besmislen.
    const [dateFrom, setDateFrom] = useState(`${new Date().getFullYear()}-01-01`);
    const [dateTo, setDateTo] = useState('');
    const [q, setQ] = useState('');
    const [qLive, setQLive] = useState('');
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState('document_date');
    const [dir, setDir] = useState<'asc' | 'desc'>('desc');
    const [groupBy, setGroupBy] = useState<'' | 'partner' | 'prefix' | 'category'>('');
    const [groups, setGroups] = useState<IraGroup[] | null>(null);
    const [expanded, setExpanded] = useState<Record<string, IraRow[] | 'loading'>>({});
    const sortBy = (k: string) => { if (sort === k) { setDir((d) => (d === 'asc' ? 'desc' : 'asc')); } else { setSort(k); setDir(k === 'customer_name' ? 'asc' : 'desc'); } setPage(1); };
    const arrow = (k: string) => sort === k ? (dir === 'asc' ? ' ▲' : ' ▼') : '';

    const params = React.useCallback((extra?: Record<string, string>) => {
        const p = new URLSearchParams(extra ?? {});
        if (company) p.set('company_id', company);
        if (prefix) p.set('prefix', prefix);
        if (role) p.set('role', role);
        if (region) p.set('region', region);
        if (b2) p.set('b2', b2);
        if (category) p.set('category', category);
        if (status) p.set('status', status);
        if (dateFrom) p.set('date_from', dateFrom);
        if (dateTo) p.set('date_to', dateTo);
        if (q) p.set('q', q);
        return p;
    }, [company, prefix, role, region, b2, category, status, dateFrom, dateTo, q]);

    React.useEffect(() => { const h = setTimeout(() => { setQ(qLive); setPage(1); }, 350); return () => clearTimeout(h); }, [qLive]);
    React.useEffect(() => {
        fetch(`/api/ira-invoices?${params({ page: String(page), sort, dir, per_page: '100' }).toString()}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(async (r) => { if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.message ?? `HTTP ${r.status}`); } return r.json(); })
            .then((d) => { setData(d); setErr(''); })
            .catch((e) => setErr(e instanceof Error ? e.message : 'Greška'));
    }, [token, params, page, sort, dir]);
    React.useEffect(() => {
        if (!groupBy) return;
        setGroups(null); setExpanded({});
        fetch(`/api/ira-invoices/grouped?${params({ group_by: groupBy }).toString()}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then((d) => setGroups(d.groups ?? []))
            .catch((e) => setErr(e instanceof Error ? e.message : 'Greška'));
    }, [token, groupBy, params]);

    const expand = (g: string) => {
        if (expanded[g]) { setExpanded((p) => { const n = { ...p }; delete n[g]; return n; }); return; }
        setExpanded((p) => ({ ...p, [g]: 'loading' }));
        fetch(`/api/ira-invoices?${params({ page: '1', per_page: '200', sort: 'amount_eur', dir: 'desc', group_by: groupBy || 'partner', group_value: g }).toString()}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : Promise.reject(new Error('HTTP')))
            .then((d) => setExpanded((prev) => ({ ...prev, [g]: d.rows ?? [] })))
            .catch(() => setExpanded((prev) => { const n = { ...prev }; delete n[g]; return n; }));
    };

    const dl = async (format: 'xlsx' | 'pdf') => {
        try {
            const p = params({ format, sort, dir });
            if (groupBy) p.set('group_by', groupBy);
            const r = await fetch(`/api/ira-invoices/export?${p.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const blob = await r.blob(); const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `ira_${groupBy ? 'grupirano' : 'registar'}.${format}`; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { setErr(e instanceof Error ? e.message : 'Greška'); }
    };

    const fmt = (n: number) => new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(n) + ' €';
    const fmtD = (d: string | null) => d ? d.slice(0, 10).split('-').reverse().join('.') + '.' : '—';
    const pages = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1;
    const th: React.CSSProperties = { padding: '8px 10px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
    const td: React.CSSProperties = { padding: '6px 10px' };
    const STAT: Record<string, { hr: string; fg: string; bg: string }> = {
        placen: { hr: 'PLAĆEN', fg: '#3fae6b', bg: 'rgba(63,174,107,0.16)' },
        otvoren: { hr: 'OTVOREN', fg: '#c05a5a', bg: 'rgba(192,90,90,0.16)' },
        djelomicno: { hr: 'DJELOMIČNO', fg: '#d8a24a', bg: 'rgba(216,162,74,0.16)' },
        odobrenje: { hr: 'ODOBRENJE', fg: '#9a9488', bg: 'rgba(154,148,136,0.16)' },
        maloprodaja: { hr: 'MP (40.3)', fg: '#9a9488', bg: 'rgba(154,148,136,0.16)' },
    };

    const table = (rows: IraRow[], compact = false) => (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: compact ? 11.5 : 12.5 }}>
            {!compact ? (
                <thead><tr style={{ textAlign: 'left', color: T.dim, fontSize: 11, position: 'sticky', top: 0, background: T.panel, zIndex: 1 }}>
                    <th style={th} onClick={() => sortBy('company')}>{t('Firma', 'Co.')}{arrow('company')}</th>
                    <th style={th} onClick={() => sortBy('document_date')}>{t('Datum', 'Date')}{arrow('document_date')}</th>
                    <th style={th} onClick={() => sortBy('customer_name')}>{t('Kupac', 'Customer')}{arrow('customer_name')}</th>
                    <th style={th} onClick={() => sortBy('invoice_number')}>{t('Račun', 'Invoice')}{arrow('invoice_number')}</th>
                    <th style={{ ...th, cursor: 'default' }}>{t('Kategorija', 'Category')}</th>
                    <th style={{ ...th, textAlign: 'right' }} onClick={() => sortBy('amount_eur')}>{t('Iznos', 'Amount')}{arrow('amount_eur')}</th>
                    <th style={{ ...th, cursor: 'default' }}>Status</th>
                </tr></thead>
            ) : null}
            <tbody>
                {rows.map((r) => {
                    const st = STAT[r.status] ?? STAT.otvoren;
                    return (
                        <tr key={r.id} style={{ borderTop: `1px solid ${T.line}` }}>
                            <td style={td}>{r.company_code}</td>
                            <td style={{ ...td, color: T.dim, whiteSpace: 'nowrap' }}>{fmtD(r.document_date)}</td>
                            <td style={{ ...td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {r.is_retail ? '🔒 ' : ''}{r.customer_name ?? '—'}
                            </td>
                            <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: T.dim, whiteSpace: 'nowrap' }}>{r.invoice_number}</td>
                            <td style={{ ...td, fontSize: 11, whiteSpace: 'nowrap' }} title={`${r.doc_label} (Faros ${r.doc_prefix ?? '?'})`}>
                                <span style={{ color: (r.category ?? '').includes('storno') ? '#9a9488' : (r.category === 'otkup' ? '#c05a5a' : (r.category === 'rafinerija' || r.category === 'prihod' ? '#3fae6b' : T.dim)) }}>
                                    {r.category_label ?? r.doc_label}
                                </span>
                            </td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap', color: r.amount_eur < 0 ? '#c05a5a' : T.ink }}>{fmt(r.amount_eur)}</td>
                            <td style={td}><span style={{ background: st.bg, color: st.fg, padding: '2px 8px', borderRadius: 5, fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{st.hr}</span></td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );

    const grandNeto = groups?.reduce((s, g) => s + g.neto, 0) ?? 0;
    return (
        <div style={{ color: T.ink }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>{t('Registar izlaznih računa (IRA)', 'Outgoing invoices (IRA)')}</h3>
                <span style={{ color: T.dim, fontSize: 12 }}>🔒 {t('građani anonimizirani · minus stavke = otkup/odobrenja', 'citizens anonymized')}</span>
            </div>
            {data?.summary ? (
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    {[
                        [t('Dokumenata', 'Documents'), String(data.summary.count), T.ink],
                        [t('Prihod (prodaja, neto)', 'Revenue (net)'), fmt(data.summary.prihod), '#3fae6b'],
                        [t('TCA (lom)', 'TCA (scrap)'), fmt(data.summary.tca), '#3fae6b'],
                        [t('Otkup od građana (COGS)', 'Buyback (COGS)'), fmt(data.summary.otkup), '#c05a5a'],
                        [t('Kovnice (provjeriti)', 'Mints (verify)'), fmt(data.summary.kovnica), '#d8a24a'],
                        [t('Avansi (neto)', 'Advances (net)'), fmt(data.summary.avans), T.dim],
                        [t('Otvoreno (pravne)', 'Open (legal)'), fmt(data.summary.otvoreno), '#d8a24a'],
                    ].map(([l, v, c], i) => (
                        <div key={i} style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 10, padding: '8px 12px', minWidth: 130, flex: 1 }}>
                            <div style={{ fontSize: 11, color: T.dim }}>{l}</div>
                            <div style={{ fontSize: 17, fontWeight: 700, color: c as string }}>{v}</div>
                        </div>
                    ))}
                </div>
            ) : null}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select value={company} onChange={(e) => { setCompany(e.target.value); setPage(1); }} style={selStyle}>
                    <option value="">{t('Sve tvrtke', 'All companies')}</option>
                    {(data?.companies ?? []).map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                </select>
                <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }} style={{ ...selStyle, borderColor: category ? T.gold : T.line }}
                    title={t('Poslovna kategorija — gdje minus/plus stvarno pripada', 'Business category')}>
                    <option value="">{t('Sve kategorije', 'All categories')}</option>
                    {(data?.categories ?? []).map((c) => <option key={c.key} value={c.key}>{c.label} ({c.n})</option>)}
                </select>
                <select value={prefix} onChange={(e) => { setPrefix(e.target.value); setPage(1); }} style={selStyle}
                    title={t('Vrsta dokumenta iz Farosa — značenje se razlikuje po firmi', 'Faros document type — differs per company')}>
                    <option value="">{t('Sve vrste', 'All types')}</option>
                    {(data?.prefixes ?? []).map((p, i) => (
                        <option key={`${p.company_code ?? ''}-${p.prefix}-${i}`} value={p.prefix}>
                            {p.company_code ? `${p.company_code} · ` : ''}{p.label} ({p.n}{p.hint ? `, ${p.hint}` : ''})
                        </option>
                    ))}
                </select>
                <select value={role} onChange={(e) => { setRole(e.target.value); setPage(1); }} style={selStyle}>
                    <option value="">{t('Svi kupci', 'All customers')}</option>
                    <option value="gradani">{t('Građani', 'Citizens')}</option>
                    <option value="pravne">{t('Pravne osobe', 'Legal entities')}</option>
                </select>
                <select value={region} onChange={(e) => { setRegion(e.target.value); setPage(1); }} style={selStyle} title={t('Država iz Farosovog šifrarnika partnera', 'Country from the Faros partner register')}>
                    <option value="">{t('Tuzemni + EU + izvan EU', 'All regions')}</option>
                    <option value="hr">{t('Tuzemni', 'Domestic')}</option>
                    <option value="eu">EU</option>
                    <option value="non_eu">{t('Izvan EU', 'Non-EU')}</option>
                    <option value="unknown">{t('Država nepoznata', 'Country unknown')}</option>
                </select>
                <select value={b2} onChange={(e) => { setB2(e.target.value); setPage(1); }} style={selStyle}>
                    <option value="">B2B + B2C</option>
                    <option value="b2b">B2B</option>
                    <option value="b2c">B2C</option>
                </select>
                <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={selStyle}>
                    <option value="">{t('Svi statusi', 'All statuses')}</option>
                    <option value="open">{t('Otvoreni', 'Open')}</option>
                    <option value="paid">{t('Plaćeni', 'Paid')}</option>
                    <option value="minus">{t('Minus (otkup/odobrenja)', 'Negative')}</option>
                </select>
                {(() => {
                    const Y = new Date().getFullYear();
                    const pad = (n: number) => String(n).padStart(2, '0');
                    const d = (dt: Date) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
                    const now = new Date();
                    const quick: [string, string, string][] = [
                        [t('Ova godina', 'This year'), `${Y}-01-01`, ''],
                        [t('Ovaj mjesec', 'This month'), `${Y}-${pad(now.getMonth() + 1)}-01`, ''],
                        [t('30 dana', '30 days'), d(new Date(now.getTime() - 30 * 864e5)), ''],
                        [String(Y - 1), `${Y - 1}-01-01`, `${Y - 1}-12-31`],
                        [t('Sve', 'All'), '', ''],
                    ];
                    return (
                        <span style={{ display: 'inline-flex', gap: 3 }}>
                            {quick.map(([lbl, from, to]) => {
                                const on = dateFrom === from && dateTo === to;
                                return (
                                    <button key={lbl} type="button" onClick={() => { setDateFrom(from); setDateTo(to); setPage(1); }}
                                        style={{ background: on ? 'rgba(201,162,39,0.16)' : 'transparent', border: `1px solid ${on ? T.gold : T.line}`, color: on ? T.gold : T.dim, borderRadius: 14, padding: '4px 10px', fontSize: 11.5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                        {lbl}
                                    </button>
                                );
                            })}
                        </span>
                    );
                })()}
                <DateInput value={dateFrom} onChange={(iso) => { setDateFrom(iso); setPage(1); }} style={{ ...selStyle, width: 108 }} />
                <span style={{ color: T.dim }}>→</span>
                <DateInput value={dateTo} onChange={(iso) => { setDateTo(iso); setPage(1); }} style={{ ...selStyle, width: 108 }} />
                <input value={qLive} onChange={(e) => setQLive(e.target.value)} placeholder={t('Traži kupca, račun…', 'Search…')} style={{ ...selStyle, minWidth: 170 }} />
                <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as '' | 'partner' | 'prefix' | 'category')}
                    style={{ ...selStyle, borderColor: groupBy ? T.gold : T.line, color: groupBy ? T.gold : T.ink, fontWeight: groupBy ? 600 : 400 }}>
                    <option value="">{t('Bez grupiranja', 'No grouping')}</option>
                    <option value="category">⊞ {t('Grupiraj po kategoriji', 'Group by category')}</option>
                    <option value="partner">⊞ {t('Grupiraj po partneru', 'Group by partner')}</option>
                    <option value="prefix">⊞ {t('Grupiraj po vrsti dok.', 'Group by doc type')}</option>
                </select>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button type="button" onClick={() => void dl('xlsx')} style={{ ...selStyle, cursor: 'pointer' }}>⬇ XLS</button>
                    <button type="button" onClick={() => void dl('pdf')} style={{ ...selStyle, cursor: 'pointer' }}>⬇ PDF</button>
                    {!groupBy ? (
                        <>
                            <span style={{ color: T.dim, fontSize: 12 }}>{data ? `${data.total} · ${page}/${pages}` : '…'}</span>
                            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={{ ...selStyle, cursor: 'pointer' }}>‹</button>
                            <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} style={{ ...selStyle, cursor: 'pointer' }}>›</button>
                        </>
                    ) : null}
                </span>
            </div>
            {err ? <div style={{ background: 'rgba(192,90,90,0.15)', color: '#c05a5a', padding: 10, borderRadius: 8, marginBottom: 8 }}>{err}</div> : null}
            <div style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 340px)', overflowY: 'auto' }}>
                    {!groupBy ? (
                        <>
                            {table(data?.rows ?? [])}
                            {data && data.rows.length === 0 ? <div style={{ color: T.dim, padding: 20, textAlign: 'center' }}>{t('Nema računa za zadane filtere.', 'No invoices.')}</div> : null}
                        </>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                            <thead><tr style={{ textAlign: 'left', color: T.dim, fontSize: 11, position: 'sticky', top: 0, background: T.panel, zIndex: 1 }}>
                                <th style={{ ...th, cursor: 'default' }}>{groupBy === 'partner' ? t('Partner', 'Partner') : groupBy === 'category' ? t('Kategorija', 'Category') : t('Vrsta dokumenta', 'Doc type')}</th>
                                <th style={{ ...th, cursor: 'default', textAlign: 'right' }}>{t('Dokumenata', 'Docs')}</th>
                                <th style={{ ...th, cursor: 'default', textAlign: 'right' }}>{t('Neto', 'Net')}</th>
                                <th style={{ ...th, cursor: 'default', textAlign: 'right' }}>{t('Udio', 'Share')}</th>
                                <th style={{ ...th, cursor: 'default', textAlign: 'right' }}>{t('Otvoreno', 'Open')}</th>
                            </tr></thead>
                            <tbody>
                                {groups === null ? (
                                    <tr><td colSpan={5} style={{ ...td, color: T.dim, textAlign: 'center', padding: 20 }}>{t('Učitavam…', 'Loading…')}</td></tr>
                                ) : groups.map((g) => {
                                    const exp = expanded[g.group];
                                    return (
                                        <React.Fragment key={g.group}>
                                            <tr onClick={() => expand(g.group)} style={{ borderTop: `1px solid ${T.line}`, cursor: 'pointer', background: exp ? 'rgba(201,162,39,0.08)' : undefined }}>
                                                <td style={{ ...td, fontWeight: 700 }}>{exp ? '▾' : '▸'} {g.is_citizens ? '🔒 ' : ''}{g.label}</td>
                                                <td style={{ ...td, textAlign: 'right', color: T.dim }}>{g.count}</td>
                                                <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: g.neto < 0 ? '#c05a5a' : T.ink }}>{fmt(g.neto)}</td>
                                                <td style={{ ...td, textAlign: 'right', color: T.dim }}>{grandNeto !== 0 ? (100 * g.neto / grandNeto).toFixed(1) + '%' : '—'}</td>
                                                <td style={{ ...td, textAlign: 'right', color: Math.abs(g.otvoreno) >= 0.01 ? '#d8a24a' : T.dim }}>{fmt(g.otvoreno)}</td>
                                            </tr>
                                            {exp === 'loading' ? (
                                                <tr><td colSpan={5} style={{ ...td, color: T.dim, paddingLeft: 30 }}>{t('Učitavam…', 'Loading…')}</td></tr>
                                            ) : Array.isArray(exp) ? (
                                                <tr><td colSpan={5} style={{ padding: '0 0 8px 26px', background: 'rgba(0,0,0,0.12)' }}>
                                                    {table(exp, true)}
                                                    {exp.length >= 200 ? <div style={{ color: T.dim, fontSize: 11, padding: '4px 10px' }}>{t('Prikazano prvih 200 po iznosu.', 'First 200 shown.')}</div> : null}
                                                </td></tr>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                                {groups !== null ? (
                                    <tr style={{ borderTop: `2px solid ${T.gold}`, fontWeight: 700 }}>
                                        <td style={td}>{t('UKUPNO', 'TOTAL')}</td>
                                        <td style={{ ...td, textAlign: 'right' }}>{groups.reduce((s, g) => s + g.count, 0)}</td>
                                        <td style={{ ...td, textAlign: 'right' }}>{fmt(grandNeto)}</td>
                                        <td style={{ ...td, textAlign: 'right' }}>100%</td>
                                        <td style={{ ...td, textAlign: 'right' }}>{fmt(groups.reduce((s, g) => s + g.otvoreno, 0))}</td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Izvori CF projekcije — superadmin uključuje/isključuje pojedini forecast tok (npr. TCA/dobavljači dok knjiženja nisu točna). */
const FC_SOURCES: { key: string; label: string; sign: '+' | '−'; hint: string }[] = [
    { key: 'gold_client_order', label: 'Naplata isporuka klijentima', sign: '+', hint: 'potvrđene narudžbe iz plan & generate' },
    { key: 'tca_scrap', label: 'TCA izvoz loma', sign: '+', hint: 'otkup × 1,20, +7 dana (privremeno)' },
    { key: 'gold_supplier', label: 'Nabava zlata (dobavljači)', sign: '−', hint: 'potvrđene narudžbe (privremeno)' },
    { key: 'forecast_buyback', label: 'Otkup loma (forecast)', sign: '−', hint: 'statistička dnevna procjena' },
    { key: 'forecast_investment', label: 'Otkup investicijskog (forecast)', sign: '−', hint: 'statistička dnevna procjena' },
    { key: 'forecast_sales_trend', label: 'Forecast prodaje (modul 1)', sign: '+', hint: 'prosjek 3 mj. × sezonski faktor prošle godine (0,5-2,0); uključen zamjenjuje WMA ispod' },
    { key: 'sales_plan', label: 'Plan prodaje', sign: '+', hint: 'puni budući modul planiranja prodaje' },
    { key: 'forecast_sales_investment', label: 'Prodaja investicijskog (forecast)', sign: '+', hint: 'ADO ponude, WMA po danu u tjednu; ne vrijedi dok je forecast modula 1 uključen' },
    { key: 'ura_obligation', label: 'URA računi (obveze)', sign: '−', hint: 'datum plaćanja ili +2 r.d.' },
    { key: 'payment_batch', label: 'Nalozi za plaćanje', sign: '−', hint: 'na datum naloga; realizirano do potvrde izvodom' },
    { key: 'trade', label: 'Trade forecast', sign: '+', hint: 'kad bude izvor' },
];

/** Treasury pragovi (bi_pt_settings). Redoslijed i opisi; ključevi = LikvidaturaSettingsController::THRESHOLDS. */
const THRESHOLD_FIELDS: { key: string; unit: '€' | 'dana'; label: string; hint: string }[] = [
    { key: 'second_signature_threshold_eur', unit: '€', label: '2. potpis po računu', hint: 'Račun iznad ovog iznosa treba 2. potpis prije naloga i virmana. Bez zadane vrijednosti: dok nije spremljen, 2. potpis treba svaki račun.' },
    { key: 'batch_dual_threshold_eur', unit: '€', label: 'Dvije ovjere naloga', hint: 'Nalog čiji zbir prelazi iznos traži dvije ovjere.' },
    { key: 'hitno_bypass_eur', unit: '€', label: 'HITNO bez ovjere', hint: 'Nalog samo od HITNO stavki ispod iznosa ovjerava se sam pri zaključavanju.' },
    { key: 'low_balance_eur', unit: '€', label: 'Nisko stanje računa', hint: 'Račun ispod iznosa je u Pregledu označen kao nizak.' },
    { key: 'gold_horizon_days', unit: 'dana', label: 'Rezerva za zlato', hint: 'Koliko dana unaprijed se obveze za zlato drže kao rezerva na nalozima.' },
    { key: 'forecast_days', unit: 'dana', label: 'Projekcija na nalozima', hint: 'Broj dana projekcije stanja uz naloge (1-90).' },
];

function ThresholdsTab({ token, onSaved, locale }: { token: string; onSaved: () => void; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [vals, setVals] = useState<Record<string, string> | null>(null);
    const [defs, setDefs] = useState<Record<string, number>>({});
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');

    const apply = (d: Record<string, unknown>) => {
        const v: Record<string, string> = {};
        THRESHOLD_FIELDS.forEach((f) => { v[f.key] = d[f.key] != null ? String(d[f.key]) : ''; });
        setVals(v);
    };
    React.useEffect(() => {
        fetch('/api/likvidatura/settings', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
            .then((d) => { apply(d); setDefs(d.threshold_defaults ?? {}); })
            .catch((e) => setMsg(t('Postavke nisu dostupne', 'Settings unavailable') + ` (${e.message})`));
    }, [token]);

    const save = async () => {
        if (!vals) return;
        setBusy(true); setMsg('');
        const body: Record<string, number> = {};
        for (const f of THRESHOLD_FIELDS) {
            const raw = (vals[f.key] ?? '').trim().replace(/\./g, '').replace(',', '.');
            if (raw === '') continue;
            const n = Number(raw);
            if (!Number.isFinite(n) || n < 0) { setMsg(`${f.label}: ${t('neispravan broj', 'invalid number')}`); setBusy(false); return; }
            body[f.key] = f.unit === 'dana' ? Math.round(n) : n;
        }
        try {
            const r = await fetch('/api/likvidatura/settings', {
                method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(body),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) { setMsg(d.message ?? 'Greška'); setBusy(false); return; }
            apply(d); setMsg(t('Spremljeno.', 'Saved.')); onSaved();
        } catch { setMsg('Greška'); }
        setBusy(false);
    };

    return (
        <div style={{ maxWidth: 1400, margin: '0 auto 16px', background: T.panel, border: `1px solid ${T.line}`, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{t('Pragovi plaćanja', 'Payment thresholds')}</div>
            <div style={{ fontSize: 12, color: T.dim, marginBottom: 12 }}>{t('Vrijednosti se primjenjuju odmah na nove radnje. U zagradi je zadana vrijednost dok postavka nije spremljena.', 'Values apply immediately to new actions. Default shown in brackets.')}</div>
            {vals === null ? <div style={{ color: T.dim }}>{msg || t('Učitavam…', 'Loading…')}</div> : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10 }}>
                    {THRESHOLD_FIELDS.map((f) => (
                        <label key={f.key} style={{ display: 'block', padding: '8px 10px', borderRadius: 8, border: `1px solid ${T.line}` }}>
                            <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{f.label}</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '6px 0' }}>
                                <input type="text" inputMode="decimal" value={vals[f.key] ?? ''} onChange={(e) => setVals((p) => ({ ...(p ?? {}), [f.key]: e.target.value }))}
                                    style={{ ...selStyle, width: 140, textAlign: 'right' }} />
                                <span style={{ color: T.dim, fontSize: 12 }}>{f.unit}</span>
                                {defs[f.key] != null ? <span style={{ color: T.dim, fontSize: 11 }}>({t('zadano', 'default')} {defs[f.key]})</span>
                                    : (vals[f.key] ?? '') === '' ? <span style={{ color: T.gold, fontSize: 11 }}>{t('nije postavljeno', 'not set')}</span> : null}
                            </span>
                            <span style={{ display: 'block', fontSize: 11, color: T.dim }}>{f.hint}</span>
                        </label>
                    ))}
                </div>
            )}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12 }}>
                <button type="button" disabled={busy || !vals} onClick={() => void save()} style={{ background: T.gold, color: '#1c1e22', border: 0, borderRadius: 7, padding: '6px 16px', fontWeight: 700, cursor: 'pointer' }}>{t('Spremi', 'Save')}</button>
                {msg && vals !== null ? <span style={{ color: T.gold, fontSize: 13 }}>{msg}</span> : null}
            </div>
        </div>
    );
}

function ForecastSourcesPanel({ token, onSaved, locale }: { token: string; onSaved: () => void; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [open, setOpen] = useState(false);
    const [gated, setGated] = useState(false);
    const [src, setSrc] = useState<Record<string, boolean> | null>(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');

    const load = React.useCallback(() => {
        fetch('/api/likvidatura/settings', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => { if (r.status === 403) { setGated(true); return null; } return r.json(); })
            .then((d) => { if (!d) return; setSrc(d.cashflow_forecast_sources ?? {}); })
            .catch(() => setGated(true));
    }, [token]);
    React.useEffect(() => { if (open && src === null && !gated) load(); }, [open, src, gated, load]);

    const toggle = (k: string) => setSrc((p) => ({ ...(p ?? {}), [k]: !(p?.[k] ?? true) }));
    const save = async () => {
        if (!src) return;
        setBusy(true); setMsg('');
        try {
            const r = await fetch('/api/likvidatura/settings', {
                method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ cashflow_forecast_sources: src }),
            });
            if (!r.ok) { const e = await r.json().catch(() => ({})); setMsg(e.message ?? 'Greška'); setBusy(false); return; }
            setMsg(t('Spremljeno.', 'Saved.')); onSaved();
        } catch { setMsg('Greška'); }
        setBusy(false);
    };

    if (gated) return null; // samo superadmin
    return (
        <div style={{ maxWidth: 1400, margin: '0 auto 8px' }}>
            <button type="button" onClick={() => setOpen((o) => !o)}
                style={{ background: 'transparent', border: `1px solid ${T.line}`, color: T.dim, borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12.5 }}>
                ⚙ {t('Izvori projekcije', 'Projection sources')} {open ? '▲' : '▼'}
            </button>
            {open ? (
                <div style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 10, padding: 14, marginTop: 8 }}>
                    <div style={{ fontSize: 12, color: T.dim, marginBottom: 10 }}>{t('Uključi/isključi pojedini tok forecasta. TCA i dobavljači zlata su privremeni dok se knjiženja ne poprave.', 'Toggle each forecast flow. TCA and gold suppliers are temporary until bookings are fixed.')}</div>
                    {src === null ? <div style={{ color: T.dim }}>{t('Učitavam…', 'Loading…')}</div> : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
                            {FC_SOURCES.map((s) => {
                                const on = src[s.key] ?? true;
                                return (
                                    <label key={s.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', borderRadius: 8, border: `1px solid ${on ? T.gold : T.line}`, background: on ? 'rgba(201,162,39,0.08)' : 'transparent', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={on} onChange={() => toggle(s.key)} style={{ marginTop: 2 }} />
                                        <span style={{ minWidth: 0 }}>
                                            <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}><span style={{ color: s.sign === '+' ? '#3fae6b' : '#c05a5a' }}>{s.sign}</span> {s.label}</span>
                                            <span style={{ display: 'block', fontSize: 11, color: T.dim }}>{s.hint}</span>
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12 }}>
                        <button type="button" disabled={busy || !src} onClick={() => void save()} style={{ background: T.gold, color: '#1c1e22', border: 0, borderRadius: 7, padding: '6px 16px', fontWeight: 700, cursor: 'pointer' }}>{t('Spremi', 'Save')}</button>
                        {msg ? <span style={{ color: T.gold, fontSize: 13 }}>{msg}</span> : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

const selStyle: React.CSSProperties = { background: T.panel, color: T.ink, border: `1px solid ${T.line}`, borderRadius: 8, padding: '6px 10px', fontSize: 13 };
